/**
 * AdminAudioPreviewTab — upload an audio file and preview the full
 * pipeline (anonymization, STT, correction) without running a real
 * dictation. Useful for debugging and comparing pipeline changes.
 */

import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { API_URL } from "@/lib/env";
import { getAccessToken } from "@/lib/auth";

interface PreviewAnalysis {
  original_duration_s?: number;
  processed_duration_s?: number;
  anonymized?: boolean;
  mcadams_coeff?: number;
  stt_transcript?: string;
  corrected_text?: string;
  steps?: Array<{ name: string; duration_ms: number }>;
}

export function AdminAudioPreviewTab() {
  const [file, setFile] = useState<File | null>(null);
  const [originalUrl, setOriginalUrl] = useState("");
  const [processedUrl, setProcessedUrl] = useState("");
  const [analysis, setAnalysis] = useState<PreviewAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [includeAnon, setIncludeAnon] = useState(true);
  const [speedFactor, setSpeedFactor] = useState(1.0);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f) {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
      setOriginalUrl(URL.createObjectURL(f));
    }
  }, [originalUrl]);

  const runPreview = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    setProcessedUrl("");
    setAnalysis(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Niet ingelogd");
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("include_anonymization", includeAnon ? "true" : "false");
      formData.append("speed_factor", String(speedFactor));

      const resp = await fetch(`${API_URL}/api/admin/audio-preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!resp.ok) {
        let detail = `${resp.status} ${resp.statusText}`;
        try {
          const body = (await resp.json()) as { detail?: string };
          detail = body.detail ?? detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const data = (await resp.json()) as PreviewAnalysis & {
        processed_audio_base64?: string;
        processed_mime?: string;
      };
      setAnalysis(data);
      if (data.processed_audio_base64) {
        const bytes = Uint8Array.from(atob(data.processed_audio_base64), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([bytes], { type: data.processed_mime ?? "audio/wav" });
        if (processedUrl) URL.revokeObjectURL(processedUrl);
        setProcessedUrl(URL.createObjectURL(blob));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview mislukt");
    } finally {
      setLoading(false);
    }
  }, [file, includeAnon, speedFactor, processedUrl]);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-bold text-[var(--cs-navy)]">Audio-pipeline preview</h2>
        <p className="mb-4 text-xs text-[var(--cs-ink-muted)]">
          Upload een audiobestand en bekijk de output van elke pipeline-stap
          (anonimisatie, STT, correctie). Geen opslag op server.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="audio-file">
              Audiobestand
            </label>
            <input
              ref={inputRef}
              id="audio-file"
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              className="mt-1 block w-full rounded-lg border border-[rgba(10,52,96,0.18)] bg-white px-3 py-2 text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-[var(--cs-navy)]">
              <input
                type="checkbox"
                checked={includeAnon}
                onChange={(e) => setIncludeAnon(e.target.checked)}
                className="h-3 w-3"
              />
              Inclusief stemanonimisatie
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--cs-navy)]">
              Snelheid:
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="2.0"
                value={speedFactor}
                onChange={(e) => setSpeedFactor(Number(e.target.value))}
                className="w-16 rounded border border-[rgba(10,52,96,0.18)] px-2 py-0.5 text-xs"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={!file || loading}
            className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
          >
            {loading ? "Verwerken…" : "Preview genereren"}
          </button>
        </div>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {originalUrl || processedUrl ? (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {originalUrl ? (
            <div className="rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                Origineel
              </p>
              <audio
                controls
                src={originalUrl}
                className="mt-2 w-full"
                aria-label="Origineel audiobestand"
              >
                <track kind="captions" />
              </audio>
            </div>
          ) : null}
          {processedUrl ? (
            <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                Geanonimiseerd / verwerkt
              </p>
              <audio
                controls
                src={processedUrl}
                className="mt-2 w-full"
                aria-label="Verwerkt audiobestand"
              >
                <track kind="captions" />
              </audio>
            </div>
          ) : null}
        </section>
      ) : null}

      {analysis ? (
        <section className="rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-sm">
          <h3 className="mb-3 text-sm font-bold text-[var(--cs-navy)]">Pipeline analyse</h3>
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            {analysis.original_duration_s !== undefined ? (
              <MetaStat label="Origineel" value={`${analysis.original_duration_s.toFixed(1)}s`} />
            ) : null}
            {analysis.processed_duration_s !== undefined ? (
              <MetaStat
                label="Verwerkt"
                value={`${analysis.processed_duration_s.toFixed(1)}s`}
              />
            ) : null}
            {analysis.anonymized !== undefined ? (
              <MetaStat label="Geanonimiseerd" value={analysis.anonymized ? "Ja" : "Nee"} />
            ) : null}
            {analysis.mcadams_coeff !== undefined ? (
              <MetaStat label="McAdams coëff" value={String(analysis.mcadams_coeff)} />
            ) : null}
          </div>
          {analysis.stt_transcript ? (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                STT transcript
              </p>
              <p className="mt-1 rounded bg-[var(--cs-ivory)] p-3 text-xs text-[var(--cs-navy)]">
                {analysis.stt_transcript}
              </p>
            </div>
          ) : null}
          {analysis.corrected_text ? (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                Gecorrigeerde tekst
              </p>
              <p className="mt-1 rounded bg-[var(--cs-ivory)] p-3 text-xs text-[var(--cs-navy)]">
                {analysis.corrected_text}
              </p>
            </div>
          ) : null}
          {analysis.steps && analysis.steps.length > 0 ? (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                Stappen
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-[rgba(10,52,96,0.78)]">
                {analysis.steps.map((s, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{s.name}</span>
                    <span className="font-mono">{s.duration_ms} ms</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function MetaStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[var(--cs-ivory)] p-2">
      <p className="text-[9px] uppercase tracking-wider text-[var(--cs-ink-muted)]">{label}</p>
      <p className="mt-0.5 font-semibold text-[var(--cs-navy)]">{value}</p>
    </div>
  );
}
